import {
  DisplayRightsCompensationModel,
  DisplayRightsDeliveryChannel,
  DISPLAY_RIGHTS_MAX_DURATION_MONTHS,
  DISPLAY_RIGHTS_MAX_NOTE_LENGTH,
  DISPLAY_RIGHTS_MIN_DURATION_MONTHS,
  type DisplayRightsCompositionContextResponse,
} from '@fluenceur/types';
import { z } from 'zod';

/** Default run time offered when nothing pre-fills the composer. */
export const DISPLAY_RIGHTS_DEFAULT_DURATION_MONTHS = 3;

/**
 * Floor of the monthly amount, in major currency units. The server only refuses
 * below one cent; a whole unit is the honest floor for a compensation offer and
 * keeps the field free of fractional-cent noise.
 */
export const DISPLAY_RIGHTS_MIN_AMOUNT = 1;

/** Digits, with at most two decimals behind a dot or a comma. */
const AMOUNT_PATTERN = /^\d+([.,]\d{1,2})?$/;

/** Major currency units as typed -> integer cents for the API. */
export function toAmountCents(raw: string): number {
  return Math.round(Number(raw.trim().replace(',', '.')) * 100);
}

/** Integer cents -> the string the amount field shows. */
export function fromAmountCents(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/**
 * The composer form. Messages are i18n keys, translated where the field renders,
 * so the schema stays a pure module the tests can exercise without a provider.
 */
export const displayRightsComposerSchema = z.object({
  amount: z
    .string()
    .trim()
    .min(1, 'displayRights.composer.errors.amountRequired')
    .refine((value) => AMOUNT_PATTERN.test(value), 'displayRights.composer.errors.amountInvalid')
    .refine(
      (value) => toAmountCents(value) >= DISPLAY_RIGHTS_MIN_AMOUNT * 100,
      'displayRights.composer.errors.amountMin'
    ),
  durationMonths: z
    .number()
    .int()
    .min(DISPLAY_RIGHTS_MIN_DURATION_MONTHS, 'displayRights.composer.errors.durationRange')
    .max(DISPLAY_RIGHTS_MAX_DURATION_MONTHS, 'displayRights.composer.errors.durationRange'),
  note: z.string().max(DISPLAY_RIGHTS_MAX_NOTE_LENGTH, 'displayRights.composer.errors.noteTooLong'),
  channel: z.nativeEnum(DisplayRightsDeliveryChannel),
});

export type DisplayRightsComposerValues = z.infer<typeof displayRightsComposerSchema>;

/**
 * The terms half of a send / revise request. `compensationModel` is monthly and
 * only monthly in V1: the flat model stays a server capability with no UI.
 */
export function toDisplayRightsTermsPayload(values: DisplayRightsComposerValues) {
  return {
    compensationModel: DisplayRightsCompensationModel.MONTHLY,
    amountCents: toAmountCents(values.amount),
    durationMonths: values.durationMonths,
    note: values.note.trim() || null,
    channel: values.channel,
  };
}

/**
 * Opening values of the composer, per the gesture the server resolved.
 *
 * A renewal pre-fills from the last accepted agreement (`prefill`); a revision
 * pre-fills from the version being replaced (`current`) — the server
 * deliberately does not send the same terms twice under two names. A first
 * proposal starts empty so nothing commits money on the brand's behalf.
 *
 * The message falls back to the default on a revision: the shared summary
 * carries the money and the dates, never the note, so there is nothing to
 * restore there.
 */
export function buildDisplayRightsDefaults(
  context: DisplayRightsCompositionContextResponse,
  defaultNote: string,
  /**
   * What a caller wants a NEW chain to start from, when it knows better than
   * the past does — the companion's renewal card, which offers the same amount
   * for the shortest run time.
   *
   * Deliberately ignored on a revision: there a version is already in play, and
   * a caller's opening values would write over terms the brand is editing or
   * has already sent.
   */
  seed?: { amountCents: number; durationMonths: number } | null
): DisplayRightsComposerValues {
  const channel = context.emailChannel.available
    ? DisplayRightsDeliveryChannel.CONNECTED_EMAIL
    : DisplayRightsDeliveryChannel.LINK_COPY;

  if (seed && context.action !== 'revise') {
    return {
      amount: fromAmountCents(seed.amountCents),
      durationMonths: seed.durationMonths,
      note: defaultNote,
      channel,
    };
  }

  if (context.action === 'renew' && context.prefill) {
    return {
      amount: fromAmountCents(context.prefill.amountCents),
      durationMonths: context.prefill.durationMonths,
      note: context.prefill.note ?? defaultNote,
      channel,
    };
  }

  if (context.action === 'revise' && context.current) {
    return {
      amount: fromAmountCents(context.current.amountCents),
      durationMonths: context.current.durationMonths,
      note: defaultNote,
      channel,
    };
  }

  return {
    amount: '',
    durationMonths: DISPLAY_RIGHTS_DEFAULT_DURATION_MONTHS,
    note: defaultNote,
    channel,
  };
}
